import { useCallback, useEffect, useState } from 'react';
import { useViewInfo } from '../../../../hooks/UseViewInfo';
import { Offcanvas } from 'react-bootstrap';
import { Flex, View, Link, DialogContainer, Dialog, Content, Header, Text } from '@adobe/react-spectrum';
import { Plus, Pen } from 'react-bootstrap-icons';
import { FilterIcon } from '../../../../components/protected/Icons/IconsLib';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../state/store';
import { useTranslation } from 'react-i18next';
import styles from './filter_component.module.css';
import FilterProductCode from './FilterProductCode';
import { useDeviceDetection } from '../../../../hooks/UseDeviceDetection';
import PaymentStatusFilter from './PaymentStatusFilter';
import InvoiceStatusFilter from './InvoiceStatusFilter';

interface IFilterComponentProps {
  onFilterChange: (filterParams: {}) => void;
  filterParams: any;
  filterStyle?: any;
  sumInvoice?: any;
  invoiceStatusList?: any[];
  SELECTED_INVOICE?: any;
}

const FilterComponent: React.FC<IFilterComponentProps> = ({ onFilterChange, filterParams, filterStyle, sumInvoice, invoiceStatusList, SELECTED_INVOICE }) => {
  const selectedApp = useSelector((state: RootState) => state.userWebLayout.selectedAppId);
  const { t } = useTranslation('invoice');
  const _t = (key: string) => t(`invoice_filters.${key}`, { ns: 'invoice' });
  const _tt = (key: string) => t(`invoice_common.${key}`, { ns: "invoice"});
  const { isMobile } = useViewInfo();
  const [show, setShow] = useState(false);
  const [selectedView, setSelectedView] = useState<string>(filterParams?.FILTER.COL.find((col: any) => col.name === 'view')?.value || 'all_invoices');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [clearFilters, setClearFilters] = useState<boolean>(false);
  const [selectedFolder, setSelectedFolder] = useState<string>("");
  const deviceInfo = useDeviceDetection(isMobile);
  const handleClose = () => setShow(false);
  const handleShow = () => {
    setShow(true);
    setSelectedView(filterParams?.FILTER.COL.find((col: any) => col.name === 'view')?.value || 'All views');
  };


  const createParams = useCallback(
    async (filterTypeName: any, filterValue: any) => {
      const filterValueStr =
        filterTypeName === "contactType"
          ? filterValue
          : Array.from(filterValue).join(",");
      if (
        (filterTypeName == "status" && filterValueStr == "All status") ||
        (filterTypeName == "state" && (filterValueStr == "" || filterValueStr == "ALL")) ||
        (filterTypeName == "paymentStatus" && (filterValueStr == "" || filterValueStr == "0"))
      ) {
        filterParams.FILTER.COL = filterParams.FILTER.COL.filter((col: any) => col.name !== filterTypeName);
        onFilterChange(filterParams);
        return;
      }
      if (filterTypeName === "view" && filterValueStr === "all_invoices") {
        filterParams.FILTER.COL = filterParams.FILTER.COL.filter((col: any) => col.name !== "view");
        onFilterChange(filterParams);
        return;
      }
      handleFilterType(filterTypeName, filterValueStr, filterValue);
    },
    [selectedApp]
  );

  const updateFilterParams = (name: string, value: string) => {
    let updatedArray = filterParams.FILTER.COL.map((obj: any) =>
      obj.name === name ? { ...obj, value } : obj
    );
    const hasNameSearch = filterParams.FILTER.COL.some(
      (obj: any) => obj.name === name
    );

    if (!hasNameSearch) {
      updatedArray.push({ name, value });
    }
    filterParams.FILTER.COL = updatedArray;
  };
  const handleFilterType = (
    filterTypeName: any,
    filterValueStr: any,
    filterValue: any
  ) => {
    switch (filterTypeName) {
      case "status":
        updateFilterParams("status", filterValueStr);
        break;
      case "state":
        updateFilterParams("state", filterValueStr);
        break;
      case "paymentStatus":
        updateFilterParams("paymentStatus", filterValueStr);
        break;
      case "view":
        updateFilterParams("view", filterValueStr);
        break;
      default:
        break;
    }

    filterParams.FILTER.COL = filterParams.FILTER.COL.filter(
      (col: any) => col.value !== ""
    );
    onFilterChange(filterParams);
  };

  const handleClearFilters = () => {
    setClearFilters(true);
    setTimeout(() => setClearFilters(false), 0);
  };

  const shouldUseMobileLayout = deviceInfo.shouldUseMobileLayout();
  const isMobileOrSmallScreen =
    shouldUseMobileLayout || deviceInfo.isIPad || deviceInfo.isTablet;
  const capitalizeFirstLetter = useCallback((value?: string) => {
    if (!value) return value || '';
    return value.charAt(0).toUpperCase() + value.slice(1);
  }, []);
  const stripAllPrefix = useCallback((value?: string) => {
    if (!value) return value || '';
    return value.replace(/^all\s+/i, '');
  }, []);
  const getCurrentViewLabel = useCallback(() => {
    switch (selectedView) {
      case 'invoice_my_approval':
        return _t('invoice_my_approval');
      case 'invoice_overdue':
        return _t('invoice_overdue');
      case 'all_invoices':
      default:
        return capitalizeFirstLetter(stripAllPrefix(SELECTED_INVOICE?.filterTitle || _t('invoice_all')));
    }
  }, [SELECTED_INVOICE?.filterTitle, selectedView, stripAllPrefix, capitalizeFirstLetter]);

  return isMobileOrSmallScreen ? (
    <View position={'relative'} width={'100%'} height={'64px'} UNSAFE_className={styles.mobile_header_parent}>
      <Flex direction={'column'} position={'absolute'} top={'calc(50% - 16px)'} left={'20px'} align-items={'start'} justifyContent={'start'}>
        <View position={'relative'} UNSAFE_className={styles.mobile_header_text_1}>
          {getCurrentViewLabel()}
        </View>
      </Flex>
      <View position={'absolute'} top={'calc(50% - 12px)'} left={'16px'} width={'24px'} height={'24px'} overflow={'hidden'}>
        <span onClick={handleShow} className="me-2">
          <i className="bi bi-list"></i>
        </span>
        <Offcanvas show={show} onHide={handleClose} placement="start">
          <Offcanvas.Header closeButton>
            <Offcanvas.Title></Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Flex
              direction={'column'}
              position={'absolute'}
              top={'50px'}
              left={'15px'}
              width={'240px'}
              alignItems={'start'}
              justifyContent={'start'}
              gap={'5px'}
              UNSAFE_className={styles.views_menu_container}
            >
              <Flex direction={'row'} UNSAFE_className={styles.views_menu_heading} alignSelf={'stretch'} alignItems={'center'} justifyContent={'start'}>
                <View position={'relative'} UNSAFE_className={styles.menu_heading_text}>
                  {_t('invoice_views')}
                </View>
              </Flex>
              <Flex direction={'column'} alignSelf={'stretch'} alignItems={'start'} justifyContent={'start'} gap={'4px'} UNSAFE_className={styles.filter_views_parent}>
                {sumInvoice && sumInvoice.all &&
                  <>
                    <a
                      onClick={() => {
                        setSelectedView('all_invoices');
                        createParams('view', new Set(['all_invoices']));
                        setShow(false);
                      }}
                    >
                      <Flex UNSAFE_className={selectedView === 'all_invoices' ? styles.filter_views_container_selected : styles.filter_views_container}>
                        <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
                          <Flex position={'relative'} direction={'column'} flex={1}>
                            <Content UNSAFE_className={styles.filter_views_text}>{SELECTED_INVOICE?.filterTitle || _t('invoice_all')}</Content>
                            <Content UNSAFE_className={styles.filter_views_amount}>czk {(+sumInvoice.all).toFixed(2)}</Content>
                          </Flex>
                        </Flex>
                      </Flex>
                    </a>
                    {sumInvoice?.forApproval && (
                      <a
                        onClick={() => {
                          setSelectedView('invoice_my_approval');
                          createParams('view', new Set(['invoice_my_approval']));
                          setShow(false);
                        }}
                      >
                        <Flex UNSAFE_className={selectedView === 'invoice_my_approval' ? styles.filter_views_container_selected : styles.filter_views_container}>
                          <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
                            <Flex position={'relative'} direction={'column'} flex={1}>
                              <Content UNSAFE_className={styles.filter_views_text}>{_t('invoice_my_approval')}</Content>
                              <Content UNSAFE_className={styles.filter_views_amount}>czk {(+sumInvoice.forApproval).toFixed(2)}</Content>
                            </Flex>
                          </Flex>
                        </Flex>
                      </a>
                    )}
                    {sumInvoice?.overdue && SELECTED_INVOICE?.baseType === "1" && (
                      <a
                        onClick={() => {
                          setSelectedView('invoice_overdue');
                          createParams('view', new Set(['invoice_overdue']));
                          setShow(false);
                        }}
                      >
                        <Flex UNSAFE_className={selectedView === 'invoice_overdue' ? styles.filter_views_container_selected : styles.filter_views_container}>
                          <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
                            <Flex position={'relative'} direction={'column'} flex={1}>
                              <Content UNSAFE_className={styles.filter_views_text}>{_t('invoice_overdue')}</Content>
                              <Content UNSAFE_className={styles.filter_views_amount}>czk {(+sumInvoice.overdue).toFixed(2)}</Content>
                            </Flex>
                          </Flex>
                        </Flex>
                      </a>
                    )}
                  </>
                }
              </Flex>
            </Flex>
          </Offcanvas.Body>
        </Offcanvas>
      </View>

      <View position={'absolute'} top={'calc(50% - 12px)'} left={'calc(100% - 40px)'} width={'24px'} height={'24px'} overflow={'hidden'}>
        <div onClick={() => setIsOpen(true)}>
          <FilterIcon size={20} />
        </div>

        <DialogContainer
          isDismissable
          onDismiss={() => {
            setIsOpen(false);
          }}
        >
          {isOpen && (
            <Dialog size="L">
              <Content>
                <Header>
                  <Flex position={'relative'} direction={'row'} justifyContent={'center'} alignContent={'center'} width={'100%'}>
                    <View position={'relative'} UNSAFE_className={styles.popup_header_text}>
                      {t('filters', { ns: 'layout_components' })}
                    </View>
                  </Flex>
                </Header>
                <View width={'100%'} position={'relative'} height={'100vh'}>
                  <Flex
                    direction={'column'}
                    position={'absolute'}
                    width={'95%'}
                    top={'30px'}
                    left={'16px'}
                    alignItems={'start'}
                    justifyContent={'start'}
                    gap={'24px'}
                    UNSAFE_className={styles.popup_header_body_parent}
                  >
                    <Flex direction={'column'} width={'100%'} alignItems={'start'} justifyContent={'start'} gap={'12px'} UNSAFE_className={styles.popup_header_body}>
                      <Flex direction={'column'} alignSelf={'stretch'} alignItems={'start'} justifyContent={'start'} gap={'4px'} UNSAFE_className={styles.filter_views_parent}>
                        {sumInvoice &&
                          <>
                            <View position={'relative'} width={'100%'} height={'20px'} UNSAFE_className={styles.activity_type_clear_all_container}>
                              <View position={'relative'} UNSAFE_className={styles.activity_type_clear_all_text}>
                                <Link onPress={() => {
                                  createParams('view', new Set(['All currencies']))
                                  setSelectedView('All currencies')
                                }}>{_t('clear_all')}</Link>
                              </View>
                            </View>
                          </>
                        }
                      </Flex>
                      <FilterProductCode createParams={createParams} filterParams={filterParams} clearFilters={clearFilters} />
                      <InvoiceStatusFilter
                        createParams={createParams}
                        filterParams={filterParams}
                        invoiceStatusList={invoiceStatusList}
                        baseType={SELECTED_INVOICE?.baseType}
                      />
                      <PaymentStatusFilter createParams={createParams} filterParams={filterParams} />
                    </Flex>
                  </Flex>
                </View>
              </Content>
            </Dialog>
          )}
        </DialogContainer>
      </View>
    </View>
  ) : (
    <View width={'100%'} position={'relative'} backgroundColor={'static-white'} UNSAFE_className={styles.filter_menu_parent} UNSAFE_style={filterStyle}>
      <Flex direction={'column'} position={'absolute'} top={'10px'} left={'0px'} width={'240px'} alignItems={'start'} justifyContent={'start'} gap={'5px'} UNSAFE_className={styles.views_menu_container}>
        <Flex direction={'row'} UNSAFE_className={styles.views_menu_heading} alignSelf={'stretch'} alignItems={'center'} justifyContent={'start'}>
          <View position={'relative'} UNSAFE_className={styles.menu_heading_text}>
            {_t('invoice_views')}
          </View>
        </Flex>
        <Flex direction={'column'} alignSelf={'stretch'} alignItems={'start'} justifyContent={'start'} gap={'4px'} UNSAFE_className={styles.filter_views_parent}>
          {sumInvoice && sumInvoice.all &&
            <a
              onClick={() => {
                setSelectedView('all_invoices');
                createParams('view', 'all_invoices');
              }}
            >
              <Flex
                direction={'row'}
                alignItems={'center'}
                justifyContent={'center'}
                UNSAFE_className={selectedView === 'all_invoices' ? styles.filter_views_container_selected : styles.filter_views_container}
              >
                <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
                  <Flex position={'relative'} direction={'column'} flex={1}>
                    <Content UNSAFE_className={styles.filter_views_text}>{SELECTED_INVOICE.filterTitle}</Content>
                    <Content UNSAFE_className={styles.filter_views_amount}>czk {sumInvoice && (+sumInvoice.all).toFixed(2)}</Content>
                  </Flex>
                </Flex>
              </Flex>
            </a>
          }
          {sumInvoice && sumInvoice.forApproval &&
            <a
              onClick={() => {
                setSelectedView('invoice_my_approval');
                createParams('view', 'invoice_my_approval');
              }}
            >
              <Flex UNSAFE_className={selectedView === 'invoice_my_approval' ? styles.filter_views_container_selected : styles.filter_views_container}>
                <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
                  <Flex position={'relative'} direction={'column'} flex={1}>
                    <Content UNSAFE_className={styles.filter_views_text}>{_t('invoice_my_approval')}</Content>
                    <Content UNSAFE_className={styles.filter_views_amount}>
                      czk {(+sumInvoice.forApproval).toFixed(2)}
                    </Content>
                  </Flex>
                </Flex>
              </Flex>
            </a>
          }
          {sumInvoice && sumInvoice.overdue && (SELECTED_INVOICE?.baseType === "1") &&
            <a
              onClick={() => {
                setSelectedView('invoice_overdue');
                createParams('view', 'invoice_overdue');
              }}
            >
              <Flex UNSAFE_className={selectedView === 'invoice_overdue' ? styles.filter_views_container_selected : styles.filter_views_container}>
                <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
                  <Flex direction={'column'} position={'relative'} flex={1} UNSAFE_className={styles.filter_views_text}>
                    <Content UNSAFE_className={styles.filter_views_text}>{_t('invoice_overdue')}</Content>
                    <Content UNSAFE_className={styles.filter_views_amount}>czk {(+sumInvoice.overdue).toFixed(2)}</Content>
                  </Flex>
                </Flex>
              </Flex>
            </a>
          }
        </Flex>
      </Flex>
      <View position={'relative'} top={'230px'} width={'100%'} height={'1px'} UNSAFE_className={styles.separator}></View>
      <Flex
        direction={'column'}
        position={'relative'}
        top={'230px'}
        left={'0px'}
        width={'240px'}
        alignItems={'start'}
        justifyContent={'start'}
        gap={'5px'}
        UNSAFE_className={styles.filters_menu_container}
      >
        <Flex direction={'row'} alignSelf={'stretch'} alignItems={'start'} justifyContent={'start'} UNSAFE_className={styles.filters_menu_wrapper}>
          <View position={'relative'} UNSAFE_className={styles.menu_heading_text}>
            {t('filters', { ns: 'layout_components' })}
          </View>
        </Flex>
        <InvoiceStatusFilter
          createParams={createParams}
          filterParams={filterParams}
          invoiceStatusList={invoiceStatusList}
          baseType={SELECTED_INVOICE?.baseType}
        />
        <PaymentStatusFilter
          createParams={createParams}
          filterParams={filterParams} />
      </Flex>
    </View>
  );
};

export default FilterComponent;
